'use client'

import { canonicalBrowserSearch } from './browser-location'
import { type ClientJourney, startClientJourney } from './metrics-browser'

/**
 * Each app supplies a finite set of public route families. Paths and query
 * values stay local for matching; only approved surface names become tags.
 * The finish boundary is URL commit, never an assertion of content readiness.
 */
export function createNavigationTelemetry(surfaces: ReadonlyArray<string>): {
  begin: (url: string, type: string) => void
  complete: (pathname: string, search: string) => void
} {
  const allowed = new Set(surfaces)
  let pending: {
    pathname: string
    search: string
    journey: ClientJourney
  } | null = null
  return {
    begin(url, type) {
      try {
        const currentUrl = `${window.location.origin}${window.location.pathname ?? '/'}${window.location.search ?? ''}`
        const destination = new URL(url, currentUrl)
        if (destination.origin !== window.location.origin) {
          return
        }
        const search = canonicalBrowserSearch(destination.search)
        if (
          destination.pathname === window.location.pathname &&
          search === canonicalBrowserSearch(window.location.search)
        ) {
          // Next discards a different pending destination even when its new
          // action returns to the current URL. Preserve this page's own commit.
          if (
            pending &&
            (pending.pathname !== destination.pathname ||
              pending.search !== search)
          ) {
            pending.journey.finish('cancelled', {
              tags: { reason: 'superseded' },
            })
            pending = null
          }
          return
        }
        pending?.journey.finish('cancelled', { tags: { reason: 'superseded' } })
        pending = null
        const segment = destination.pathname.split('/').find(Boolean) ?? 'home'
        pending = {
          pathname: destination.pathname,
          search,
          journey: startClientJourney('client.navigation.commit', {
            tags: {
              domain: 'navigation',
              surface: allowed.has(segment) ? segment : 'other',
              navigation_type: ['push', 'replace', 'traverse'].includes(type)
                ? type
                : 'other',
            },
            timeoutMs: 30_000,
          }),
        }
      } catch {
        // Routing and browser lifecycle failures are not telemetry's to throw.
      }
    },
    complete(pathname, search) {
      if (
        pending?.pathname !== pathname ||
        pending.search !== canonicalBrowserSearch(search)
      ) {
        return
      }
      pending.journey.finish('success')
      pending = null
    },
  }
}
